import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUserGlobalData } from "../../store/dreamworldSlice";
import dreamWorldProvider from "../../DreamWorldProvider";
import eventBus from "../../utils/eventBus";
import { Effect } from "../../types/effects";
import { Timestamp } from "firebase/firestore";
import VideoGenerator from "./VideoGenerator";
import ImageEditor from "./ImageEditor";
import UploadsPanel from "../uploads/UploadsPanel";
import { LibraryItem } from "../../types/library";
import {
  Image,
  Info,
  Plus,
  Trash2,
  Save,
  TestTube,
  ArrowLeft,
} from "lucide-react";

export interface Style {
  name: string;
  image: string;
}

const InfoButton: React.FC<{ content: string }> = ({ content }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
        className="ml-2 text-blue-400 hover:text-blue-300 transition-colors"
      >
        <Info size={14} />
      </button>
      {showTooltip && (
        <div className="fixed inset-x-4 md:absolute md:inset-x-auto md:w-72 md:left-0 z-50 p-3 bg-gray-900 border border-gray-600 rounded-lg shadow-xl text-xs md:text-sm text-gray-200 top-8 md:top-6">
          <div className="hidden md:block absolute -top-2 left-4 w-4 h-4 bg-gray-900 border-l border-t border-gray-600 transform rotate-45"></div>
          {content}
        </div>
      )}
    </div>
  );
};

const EffectCreationPanel: React.FC = () => {
  const userGlobalData = useSelector(selectUserGlobalData);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [prompt, setPrompt] = useState("");
  const [image, setImage] = useState("");
  const [type, setType] = useState<"photo" | "video">("photo");
  const [photosRequiredCount, setPhotosRequiredCount] = useState(1);
  const [userFlux, setUserFlux] = useState(0);
  const [styles, setStyles] = useState<Style[]>([]);
  const [newStyleName, setNewStyleName] = useState("");
  const [newStyleImage, setNewStyleImage] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [testEffect, setTestEffect] = useState<Effect | null>(null);
  const [isUploadsPanelOpen, setIsUploadsPanelOpen] = useState(false);
  const [imageToUpdate, setImageToUpdate] = useState<"main" | "style" | null>(
    null
  );
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    const handleFullScreenEnter = () => setIsFullScreen(true);
    const handleFullScreenExit = () => setIsFullScreen(false);

    eventBus.on("fullscreen:enter", handleFullScreenEnter);
    eventBus.on("fullscreen:exit", handleFullScreenExit);

    return () => {
      eventBus.off("fullscreen:enter", handleFullScreenEnter);
      eventBus.off("fullscreen:exit", handleFullScreenExit);
    };
  }, []);

  const handleAddStyle = () => {
    if (newStyleName && newStyleImage) {
      setStyles([...styles, { name: newStyleName, image: newStyleImage }]);
      setNewStyleName("");
      setNewStyleImage("");
    }
  };

  const handleRemoveStyle = (index: number) => {
    setStyles(styles.filter((_, i) => i !== index));
  };

  const handleSaveEffect = async () => {
    if (!userGlobalData) return;

    const newEffect: Omit<Effect, "id" | "useCount" | "createdAt"> = {
      title,
      description,
      prompt,
      image,
      type,
      photosRequiredCount,
      userFlux: userFlux || (type === "photo" ? 5 : 10),
      styles,
      creatorId: userGlobalData.userData.uid,
    };

    try {
      await dreamWorldProvider.createEffect(newEffect);
      // Maybe show a success message
    } catch (error) {
      console.error("Error creating effect:", error);
      // Maybe show an error message
    }
  };

  const handleTestEffect = () => {
    if (!userGlobalData) return;

    const effectToTest: Effect = {
      id: "test-effect",
      title,
      description,
      prompt,
      image,
      type,
      photosRequiredCount,
      userFlux: userFlux || (type === "photo" ? 5 : 10),
      styles,
      creatorId: userGlobalData.userData.uid,
      useCount: 0,
      createdAt: Timestamp.now(),
    };
    setTestEffect(effectToTest);
    setIsTesting(true);
  };

  const handleImageSelect = (selectedImage: LibraryItem) => {
    if (imageToUpdate === "main") {
      setImage(selectedImage.fullUrl);
    } else if (imageToUpdate === "style") {
      setNewStyleImage(selectedImage.fullUrl);
    }
    setIsUploadsPanelOpen(false);
    setImageToUpdate(null);
  };

  if (isTesting && testEffect) {
    return (
      <div className="min-h-screen bg-gray-900">
        <div className="p-4">
          <button
            onClick={() => setIsTesting(false)}
            className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft size={18} />
            <span>Back to Editor</span>
          </button>
        </div>
        <div className="px-4">
          {testEffect.type === "photo" ? (
            <ImageEditor effect={testEffect} isTesting={true} />
          ) : (
            <VideoGenerator effect={testEffect} isTesting={true} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pb-20">
      <div className="max-w-4xl mx-auto p-4">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-2">
            Create New Effect
          </h1>
          <p className="text-gray-400 text-sm">
            Design photo and video effects for the community
          </p>
        </div>

        {/* Main Form */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Title Input */}
              <div>
                <label className="flex items-center text-white font-medium mb-2 text-sm">
                  Effect Title
                  <InfoButton content="Give your effect a catchy, descriptive name that users will see first. Examples: 'Vintage Film Look', 'Cyberpunk Neon'" />
                </label>
                <input
                  type="text"
                  placeholder="e.g., Vintage Film Effect"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors text-sm"
                />
              </div>

              {/* Description Input */}
              <div>
                <label className="flex items-center text-white font-medium mb-2 text-sm">
                  Description
                  <InfoButton content="Describe what your effect does and the visual outcome users can expect. Be clear and concise." />
                </label>
                <textarea
                  placeholder="Transform photos into vintage film masterpieces..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none text-sm"
                />
              </div>

              {/* Type Selection */}
              <div>
                <label className="flex items-center text-white font-medium mb-2 text-sm">
                  Effect Type
                  <InfoButton content="Photo effects for still images or Video effects for motion. Video effects cost more and take longer to process." />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setType("photo")}
                    className={`p-3 rounded-lg border transition-colors text-sm ${
                      type === "photo"
                        ? "border-blue-400 bg-blue-400/10 text-blue-300"
                        : "border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500"
                    }`}
                  >
                    <Image size={20} className="mx-auto mb-1" />
                    <div className="font-medium">Photo</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setType("video")}
                    className={`p-3 rounded-lg border transition-colors text-sm ${
                      type === "video"
                        ? "border-blue-400 bg-blue-400/10 text-blue-300"
                        : "border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500"
                    }`}
                  >
                    <div className="w-5 h-5 mx-auto mb-1 bg-current rounded-sm"></div>
                    <div className="font-medium">Video</div>
                  </button>
                </div>
              </div>

              {/* Photos Required */}
              <div>
                <label className="flex items-center text-white font-medium mb-2 text-sm">
                  Photos Required
                  <InfoButton content="How many input images your effect needs. Most effects use 1 photo." />
                </label>
                <input
                  type="number"
                  placeholder="1"
                  value={photosRequiredCount}
                  min={1}
                  max={5}
                  onChange={(e) =>
                    setPhotosRequiredCount(
                      Math.max(1, Math.min(5, parseInt(e.target.value) || 1))
                    )
                  }
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors text-sm"
                />
              </div>

              {/* Pricing */}
              <div>
                <label className="flex items-center text-white font-medium mb-2 text-sm">
                  Flux Cost
                  <InfoButton content="Set price in Flux tokens. Leave empty for default (5 for photos, 10 for videos). You earn 60% revenue." />
                </label>
                <input
                  type="number"
                  placeholder={`Default: ${type === "photo" ? "5" : "10"} Flux`}
                  value={userFlux || ""}
                  onChange={(e) => setUserFlux(parseInt(e.target.value) || 0)}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  60% revenue share • Default: {type === "photo" ? "5" : "10"}{" "}
                  Flux
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Effect Image */}
              <div>
                <label className="flex items-center text-white font-medium mb-2 text-sm">
                  Preview Image
                  <InfoButton content="Upload a preview showing your effect result. High-quality previews get more users." />
                </label>
                <div
                  className="w-full h-32 bg-gray-700/50 border-2 border-dashed border-gray-600 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors group"
                  onClick={() => {
                    setImageToUpdate("main");
                    setIsUploadsPanelOpen(true);
                  }}
                >
                  {image ? (
                    <div className="relative w-full h-full">
                      <img
                        src={image}
                        alt="Effect Preview"
                        className="w-full h-full object-cover rounded"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 text-white text-sm">
                          Change
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400 group-hover:text-blue-400 transition-colors">
                      <Image size={24} className="mx-auto mb-1" />
                      <p className="text-sm">Select Preview</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Prompt */}
              <div>
                <label className="flex items-center text-white font-medium mb-2 text-sm">
                  AI Prompt
                  <InfoButton content="Detailed prompt telling the AI how to process images. Be specific about style, mood, colors, and visual elements." />
                </label>
                <textarea
                  placeholder="Transform into vintage film with warm sepia tones, film grain, vignette..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={6}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors resize-none text-sm"
                />
              </div>
            </div>
          </div>

          {/* Styles Section */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <div className="flex items-center mb-4">
              <h3 className="text-lg font-medium text-white">
                Style Variations
              </h3>
              <InfoButton content="Add different style options for your effect (e.g., Watercolor, Oil Painting, Impressionist styles)." />
              <span className="ml-2 text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                Optional
              </span>
            </div>

            {/* Existing Styles */}
            {styles.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-4">
                {styles.map((style, index) => (
                  <div
                    key={index}
                    className="group relative bg-gray-700 rounded-lg p-2 border border-gray-600"
                  >
                    <div className="aspect-square mb-2 rounded overflow-hidden">
                      <img
                        src={style.image}
                        alt={style.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-white text-xs text-center truncate">
                      {style.name}
                    </p>
                    <button
                      onClick={() => handleRemoveStyle(index)}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Style */}
            <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600/50">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input
                  type="text"
                  placeholder="Style name"
                  value={newStyleName}
                  onChange={(e) => setNewStyleName(e.target.value)}
                  className="md:col-span-2 p-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 text-sm"
                />
                <div
                  className="p-2 bg-gray-700 border border-gray-600 rounded flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors text-sm"
                  onClick={() => {
                    setImageToUpdate("style");
                    setIsUploadsPanelOpen(true);
                  }}
                >
                  {newStyleImage ? (
                    <div className="flex items-center space-x-2">
                      <img
                        src={newStyleImage}
                        alt="Style"
                        className="w-5 h-5 rounded object-cover"
                      />
                      <span className="text-white text-xs">Selected</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 text-gray-400">
                      <Image size={14} />
                      <span className="text-xs">Select</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleAddStyle}
                  disabled={!newStyleName || !newStyleImage}
                  className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 px-4 rounded transition-colors flex items-center justify-center space-x-1 text-sm"
                >
                  <Plus size={14} />
                  <span>Add</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Action Buttons */}
      <div
        className={`fixed ${
          isFullScreen ? "bottom-0" : "bottom-20"
        } md:bottom-0 left-0 right-0 bg-gray-800/95 backdrop-blur border-t border-gray-700 p-4`}
      >
        <div className=" mx-auto flex gap-3 justify-center">
          <button
            onClick={handleTestEffect}
            disabled={!title || !prompt}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center space-x-2 text-sm"
          >
            <TestTube size={16} />
            <span>Test Effect</span>
          </button>
          <button
            onClick={handleSaveEffect}
            disabled={!title || !description || !prompt || !image}
            className="bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center space-x-2 text-sm"
          >
            <Save size={16} />
            <span>Save Effect</span>
          </button>
        </div>
      </div>

      <UploadsPanel
        isOpen={isUploadsPanelOpen}
        onClose={() => setIsUploadsPanelOpen(false)}
        onImageSelect={handleImageSelect}
      />
    </div>
  );
};

export default EffectCreationPanel;
